import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MapPin, Star, HardHat, CheckCircle2, ArrowLeft, MessageSquare, 
  Phone, Mail, Clock, Award, MapPinned, Heart
} from 'lucide-react';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import PortfolioGallery from '../components/PortfolioGallery';
import { CompanyStatusBadge } from '../components/CompanyStatusBadge';
import { SEOHead, contractorSchema } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isPremium } = useAuth();
  const [contractor, setContractor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [badges, setBadges] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contractorRes, reviewsRes, badgesRes] = await Promise.all([
          axios.get(`${API}/users/${id}`),
          axios.get(`${API}/reviews/user/${id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/users/${id}/badges`).catch(() => ({ data: [] }))
        ]);
        setContractor(contractorRes.data);
        setReviews(reviewsRes.data || []);
        setBadges(badgesRes.data || []);
        // Check favorite status
        if (user) {
          try {
            const favRes = await axios.get(`${API}/favorites/check/${id}`, { withCredentials: true });
            setIsFavorite(favRes.data.is_favorite);
          } catch (e) { console.error('Error checking favorite:', e); }
        }
      } catch (error) {
        console.error('Error fetching contractor:', error);
        toast.error('Sous-traitant non trouvé');
        navigate('/contractors');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate, user]);

  const handleContact = () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour contacter ce sous-traitant');
      navigate('/login');
      return;
    }
    if (!isPremium) {
      toast.error('La messagerie directe nécessite un abonnement Premium');
      navigate('/dashboard/subscription');
      return;
    }
    navigate(`/dashboard/messages/${id}`);
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter');
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, { withCredentials: true });
        setIsFavorite(false);
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, { withCredentials: true });
        setIsFavorite(true);
        toast.success('Ajouté aux favoris');
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A8A]"></div>
      </div>
    );
  }

  if (!contractor) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <SEOHead
        title={`${contractor.company_name} – ${(contractor.trades || []).slice(0, 2).map((tr) => t(`trades.${tr}`)).join(', ')} à ${contractor.city || ''}`}
        description={`${contractor.company_name}, ${(contractor.trades || []).slice(0, 3).map((tr) => t(`trades.${tr}`)).join(', ')} à ${contractor.city || ''} (${contractor.country || 'Belgique'}). ${contractor.is_verified ? 'Entreprise vérifiée (BCE, assurances). ' : ''}${contractor.review_count ? `${contractor.rating?.toFixed(1)}/5 sur ${contractor.review_count} avis. ` : ''}${(contractor.description || '').slice(0, 90)}`.slice(0, 160)}
        path={`/contractors/${id}`}
        jsonLd={contractorSchema(contractor)}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/contractors')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux sous-traitants
        </Button>

        {/* Profile Header */}
        <Card className="mb-6" data-testid="contractor-profile-card">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center">
                  <HardHat className="w-12 h-12 text-[#1E3A8A]" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="contractor-name">
                    {contractor.company_name}
                  </h1>
                  {contractor.is_verified && (
                    <Badge className="badge-verified flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {t('common.verified')}
                    </Badge>
                  )}
                  {contractor.subscription_plan === 'premium' && (
                    <Badge className="badge-premium">{t('common.premium')}</Badge>
                  )}
                </div>
                <CompanyStatusBadge status={contractor.kbo_status} className="mb-3" />

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 text-[#F59E0B] fill-[#F59E0B]" />
                    <span className="font-bold text-lg">{contractor.rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-[#4B5563]">({contractor.review_count || 0} avis)</span>
                  </div>
                  {contractor.is_available ? (
                    <Badge className="bg-emerald-100 text-emerald-700">{t('common.available')}</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600">{t('common.unavailable')}</Badge>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {contractor.trades?.map((trade, i) => (
                    <Badge key={trade || i} className="badge-trade">
                      {t(`trades.${trade}`) || trade}
                    </Badge>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-[#4B5563]">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-[#1E3A8A]" />
                    {contractor.city}, {contractor.country}
                  </span>
                  {contractor.years_experience > 0 && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-[#1E3A8A]" />
                      {contractor.years_experience} ans d'expérience
                    </span>
                  )}
                  {contractor.service_radius > 0 && (
                    <span className="flex items-center gap-1">
                      <MapPinned className="w-4 h-4 text-[#1E3A8A]" />
                      Rayon: {contractor.service_radius} km
                    </span>
                  )}
                </div>

                {badges.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4" data-testid="contractor-badges">
                    {badges.map((badge) => (
                      <span
                        key={badge.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-full text-xs font-medium text-[#92400E]"
                        title={badge.description}
                        data-testid={`badge-${badge.id}`}
                      >
                        <Award className="w-3 h-3" />
                        {badge.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Contact & Favorite Buttons */}
            <div className="mt-6 pt-6 border-t border-[#1E3A8A]/10 flex gap-3">
              <Button className="btn-accent" onClick={handleContact} data-testid="contact-button">
                <MessageSquare className="w-5 h-5 mr-2" />
                Contacter
              </Button>
              <Button
                variant="outline"
                className={`gap-2 ${isFavorite ? 'border-red-300 text-red-500 hover:bg-red-50' : 'border-[#1E3A8A]/20'}`}
                onClick={toggleFavorite}
                data-testid="favorite-button"
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current text-red-500' : ''}`} />
                {isFavorite ? 'Favori' : 'Ajouter aux favoris'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="about" className="space-y-6">
          <TabsList className="bg-white border border-[#1E3A8A]/10">
            <TabsTrigger value="about">À propos</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="reviews">Avis ({reviews.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="about">
            <Card>
              <CardContent className="p-6 space-y-6">
                {contractor.description && (
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-3">Description</h3>
                    <p className="text-[#374151] whitespace-pre-wrap">{contractor.description}</p>
                  </div>
                )}

                {contractor.certifications && contractor.certifications.length > 0 && (
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-3">Certifications</h3>
                    <div className="flex flex-wrap gap-2">
                      {contractor.certifications.map((cert, i) => (
                        <Badge key={cert} variant="secondary" className="flex items-center gap-1">
                          <Award className="w-3 h-3" />
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {contractor.vat_number && (
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-3">Informations légales</h3>
                    <p className="text-[#4B5563]">TVA: {contractor.vat_number}</p>
                    {contractor.kbo && (
                      <p className="text-sm text-[#6B7280] mt-1" data-testid="contractor-kbo-info">
                        BCE : {contractor.kbo.name} · {contractor.kbo.status} — {contractor.kbo.juridical_situation}
                        {contractor.kbo_checked_at ? ` · vérifié le ${new Date(contractor.kbo_checked_at).toLocaleDateString('fr-BE')}` : ''}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="portfolio">
            <PortfolioGallery userId={id} editable={false} />
          </TabsContent>

          <TabsContent value="reviews">
            <Card>
              <CardContent className="p-6">
                {reviews.length > 0 ? (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b border-[#1E3A8A]/10 last:border-0 pb-6 last:pb-0">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium">{review.reviewer_name}</p>
                            <p className="text-sm text-[#4B5563]">
                              {new Date(review.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-5 h-5 text-[#F59E0B] fill-[#F59E0B]" />
                            <span className="font-bold">{review.overall?.toFixed(1)}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm">
                          <div>
                            <span className="text-[#4B5563]">Fiabilité:</span> {review.reliability}/5
                          </div>
                          <div>
                            <span className="text-[#4B5563]">Communication:</span> {review.communication}/5
                          </div>
                          <div>
                            <span className="text-[#4B5563]">Qualité:</span> {review.quality}/5
                          </div>
                          <div>
                            <span className="text-[#4B5563]">Délais:</span> {review.deadlines}/5
                          </div>
                        </div>
                        <p className="text-[#374151]">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#4B5563] py-8">Aucun avis pour le moment</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Availability Calendar */}
        <div className="mt-6">
          <AvailabilityCalendar userId={id} editable={false} />
        </div>
      </div>
    </div>
  );
};

export default ContractorDetailPage;
